import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Sparkles } from 'lucide-react';

import type { OrganizationUiSettings, OrgExperience, OrgLanding, OrgModule } from '../../../types';
import { DEFAULT_ORG_MODULES } from '../../../types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/Common/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Common/Card';
import { ConfirmationModal } from '@/components/Common/ConfirmationModal';
import { Label } from '@/components/Common/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/Common/select';
import { Switch } from '@/components/Common/switch';
import type { OrgUiSettingsController } from './useOrgUiSettings';

interface ExperienceCardProps {
  ui: OrgUiSettingsController;
  canEdit: boolean;
}

/** The Projects lots' modules (off by default). */
const MANAGED_MODULES = ['projects', 'gallery', 'portfolio'] as const satisfies readonly OrgModule[];
type ManagedModule = (typeof MANAGED_MODULES)[number];

const EXPERIENCES: readonly OrgExperience[] = ['etabli', 'olise'];

/** Landing pages and the module each one needs (null: always offered). */
const LANDINGS: readonly { value: OrgLanding; module: ManagedModule | null; labelKey: string }[] = [
  { value: '/welcome', module: null, labelKey: 'orgExperience.landing.welcome' },
  { value: '/projets', module: 'projects', labelKey: 'orgExperience.landing.projects' },
  { value: '/portefeuille', module: 'portfolio', labelKey: 'orgExperience.landing.portfolio' },
];
const AUTO_LANDING = '__auto__';

/** Mirrors backend OLISE_PRESET (app/core/modules.py). */
const OLISE_PRESET_APP_NAME = 'Olisé';

const moduleOn = (s: OrganizationUiSettings, module: OrgModule): boolean =>
  s.modules?.[module] ?? DEFAULT_ORG_MODULES[module];

/**
 * "Experience" (org admins): the one-click Olisé preset as a starting point,
 * then experience, Projects modules and landing page. Every control saves at
 * once through the shared ui-settings controller.
 */
export const ExperienceCard: React.FC<ExperienceCardProps> = ({ ui, canEdit }) => {
  const { t } = useTranslation();
  const [presetOpen, setPresetOpen] = useState(false);
  const { saving, save } = ui;

  const handleModule = (module: ManagedModule, checked: boolean) => {
    const modules: Partial<Record<OrgModule, boolean>> = {};
    modules[module] = checked;
    void save(`module:${module}`, { modules }, 'orgModules.saved');
  };

  const handleApplyPreset = async () => {
    if (await ui.applyOlisePreset()) setPresetOpen(false);
  };

  const disabled = !canEdit || saving !== null;
  const spinner = (key: string) =>
    saving === key ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null;

  const renderBody = (s: OrganizationUiSettings) => {
    const landingOptions = LANDINGS.filter(
      (l) => l.module === null || moduleOn(s, l.module) || l.value === s.landing
    );

    return (
      <div className="space-y-6">
        {/* Experience */}
        <section className="space-y-2">
          <h4 className="flex items-center gap-2 text-sm font-medium">
            {t('orgExperience.experience.label')}
            {spinner('experience')}
          </h4>
          <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('orgExperience.experience.label')}>
            {EXPERIENCES.map((experience) => {
              const selected = s.experience === experience;
              return (
                <button
                  key={experience}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={disabled}
                  onClick={() => !selected && void save('experience', { experience })}
                  className={cn(
                    'rounded-md border p-3 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                    selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
                  )}
                >
                  <span className="flex items-center gap-2 font-medium">
                    {t(`orgExperience.experience.${experience}.label`)}
                    {selected && <Check className="h-4 w-4 text-primary" />}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {t(`orgExperience.experience.${experience}.help`)}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Modules */}
          <section className="space-y-3">
            <h4 className="text-sm font-medium">{t('orgExperience.modules.label')}</h4>
            <ul className="space-y-3">
              {MANAGED_MODULES.map((module) => (
                <li key={module}>
                  <label className="flex items-start gap-3 text-sm">
                    <Switch
                      checked={moduleOn(s, module)}
                      onCheckedChange={(checked) => handleModule(module, checked)}
                      disabled={disabled}
                      aria-busy={saving === `module:${module}`}
                      className="mt-0.5"
                    />
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 font-medium">
                        {t(`orgModules.modules.${module}.label`)}
                        {spinner(`module:${module}`)}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {t(`orgModules.modules.${module}.help`)}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </section>

          {/* Landing page */}
          <section className="space-y-2">
            <Label htmlFor="org-landing" className="flex items-center gap-2">
              {t('orgExperience.landing.label')}
              {spinner('landing')}
            </Label>
            <Select
              value={s.landing ?? AUTO_LANDING}
              disabled={disabled}
              onValueChange={(value) =>
                void save('landing', { landing: value === AUTO_LANDING ? null : (value as OrgLanding) })
              }
            >
              <SelectTrigger id="org-landing" className="w-full sm:w-60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO_LANDING}>{t('orgExperience.landing.auto')}</SelectItem>
                {landingOptions.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    {t(l.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('orgExperience.landing.help')}</p>
          </section>
        </div>
      </div>
    );
  };

  const existingAppName = ui.settings?.app_name?.trim();

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="text-base">{t('orgExperience.title')}</CardTitle>
          <CardDescription>{t('orgExperience.description')}</CardDescription>
        </div>
        {/* The confirmation lists what the preset changes, so the button needs no help text. */}
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => setPresetOpen(true)}
          disabled={disabled || !ui.settings}
        >
          <Sparkles className="mr-2 h-4 w-4" />
          {t('orgExperience.preset.button')}
        </Button>
      </CardHeader>
      <CardContent>{ui.settings && renderBody(ui.settings)}</CardContent>
      <ConfirmationModal
        open={presetOpen}
        variant="info"
        title={t('orgExperience.preset.confirmTitle')}
        message={t('orgExperience.preset.confirmIntro')}
        confirmText={t('orgExperience.preset.confirm')}
        cancelText={t('global.cancel')}
        loading={saving === 'preset'}
        onConfirm={() => void handleApplyPreset()}
        onCancel={() => setPresetOpen(false)}
      >
        <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
          <li>{t('orgExperience.preset.items.experience')}</li>
          <li>{t('orgExperience.preset.items.modules')}</li>
          <li>{t('orgExperience.preset.items.knowledge')}</li>
          <li>{t('orgExperience.preset.items.theme')}</li>
          <li>{t('orgExperience.preset.items.landing')}</li>
          <li>
            {existingAppName
              ? t('orgExperience.preset.items.appNameKept', { name: existingAppName })
              : t('orgExperience.preset.items.appName', { name: OLISE_PRESET_APP_NAME })}
          </li>
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">{t('orgExperience.preset.outro')}</p>
      </ConfirmationModal>
    </Card>
  );
};

export default ExperienceCard;
